import React, { useCallback, useMemo, useState } from 'react';
import { RowCentered, Text } from '@ydistri/ds';
import { Dropdown, DropdownProps } from 'antd';
import MoreIconButton from '../../../components/buttons/MoreIconButton';
import { icons, IconType } from '../../../components/icons/icons';
import AsoScenarioSelect, { AsoScenarioSelectProps } from '../components/AsoScenarioSelect';
import CreateAsoScenarioModal from './CreateAsoScenarioModal';
import EditAsoScenarioModal from './EditAsoScenarioModal';
import DeleteAsoScenarioModal from './DeleteAsoScenarioModal';
import { useGetAsoScenarioListQuery } from '../apiAso';
import { useStableParams } from '../../../routes/useStableParams';
import { useNavigate } from 'react-router';
import { generatePartialPath } from '../../../routes/routerSlice';
import { ROUTES } from '../../../components/menu/getMenuLeftItems';
import { CreateAsoButton } from '../components/CreateAsoButton';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';

enum AsoAction {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  DELETE = 'DELETE',
}

const dropdownAlign: DropdownProps['align'] = { offset: [0, -6] };

interface AsoScenarioProps {
  asoRequestId?: number;
  backgroundVariant?: AsoScenarioSelectProps['backgroundVariant'];
}

const AsoScenario: React.FC<AsoScenarioProps> = ({ backgroundVariant = 'default' }) => {
  const { projectShortName, asoRequestId, asoScenarioId } = useStableParams();

  const [openedActionModal, setOpenedActionModal] = useState<AsoAction | undefined>(undefined);
  const { data: asoScenarioList } = useGetAsoScenarioListQuery(undefined);
  const navigate = useNavigate();
  const { isLocked } = useAsoScenarioStatus();
  const modificationEnabled = asoScenarioId && !isLocked;

  const asoRequestScenarios = useMemo(() => {
    if (!asoRequestId || !asoScenarioList) return [];
    return asoScenarioList.filter(a => a.asoRequestId === asoRequestId);
  }, [asoRequestId, asoScenarioList]);

  // eslint-disable-next-line @ydistri/react/no-primitive-usememo -- there may be many scenarios, we do not want to iterate them on every render
  const currentAsoName = useMemo(() => {
    if (!asoScenarioId) return undefined;
    return asoRequestScenarios.find(a => a.id === asoScenarioId)?.name;
  }, [asoRequestScenarios, asoScenarioId]);

  const activeActionModal = useMemo(() => {
    if (
      isLocked &&
      (openedActionModal === AsoAction.EDIT || openedActionModal === AsoAction.DELETE)
    ) {
      return undefined;
    }

    return openedActionModal;
  }, [isLocked, openedActionModal]);

  const dropdownMenu: DropdownProps['menu'] = useMemo(() => {
    return {
      items: [
        {
          label: 'Edit current ASO scenario',
          key: '1',
          icon: icons[IconType.EDIT_OUTLINED],
          onClick: () => setOpenedActionModal(AsoAction.EDIT),
          disabled: !modificationEnabled,
        },
        {
          label: 'Create new',
          key: '2',
          icon: icons[IconType.PLUS_OUTLINED],
          onClick: () => setOpenedActionModal(AsoAction.CREATE),
          disabled: !asoRequestId,
        },
        { type: 'divider' as const },
        {
          label: 'Delete',
          key: '3',
          icon: icons[IconType.DELETE_OUTLINED],
          onClick: () => setOpenedActionModal(AsoAction.DELETE),
          danger: true,
          disabled: !modificationEnabled,
        },
      ],
    };
  }, [asoRequestId, modificationEnabled]);

  const handleAsoChange = useCallback(
    (value: number | undefined) => {
      if (!projectShortName || !asoRequestId) return;

      const params: Record<string, string | undefined> = {
        projectShortName,
        asoRequestId: `${asoRequestId}`,
      };
      if (value !== undefined) {
        params.asoScenarioId = String(value);
      }

      const path = generatePartialPath(ROUTES.asoChildRoute, params, undefined);
      navigate(path);
    },
    [projectShortName, navigate, asoRequestId],
  );

  return (
    <>
      <RowCentered $gap="0.5rem">
        <Text $bold $width="8rem">
          ASO Scenario:{' '}
        </Text>
        {asoRequestScenarios.length > 0 && asoRequestId && (
          <>
            <AsoScenarioSelect
              onChange={value => handleAsoChange(value)}
              asoScenarioId={asoScenarioId}
              createButtonAction={() => setOpenedActionModal(AsoAction.CREATE)}
              backgroundVariant={backgroundVariant}
              asoRequestId={asoRequestId}
              placeholder="Select ASO Scenario"
            />
            <Dropdown trigger={['click']} menu={dropdownMenu} align={dropdownAlign}>
              <div>
                <MoreIconButton />
              </div>
            </Dropdown>
          </>
        )}
        {!asoRequestId && <Text>Select ASO request first</Text>}
        {asoRequestId && asoRequestScenarios.length === 0 && (
          <CreateAsoButton
            onClick={() => setOpenedActionModal(AsoAction.CREATE)}
            title="Create ASO scenario"
            disabled={!asoRequestId}
          />
        )}
      </RowCentered>

      {activeActionModal === AsoAction.CREATE && (
        <CreateAsoScenarioModal
          open={true}
          asoRequestId={asoRequestId}
          onCancel={() => setOpenedActionModal(undefined)}
        />
      )}

      {activeActionModal === AsoAction.EDIT && asoScenarioId && !isLocked && (
        <EditAsoScenarioModal
          open={true}
          asoScenarioId={asoScenarioId}
          currentName={currentAsoName}
          onCancel={() => setOpenedActionModal(undefined)}
        />
      )}

      {activeActionModal === AsoAction.DELETE && asoScenarioId && !isLocked && (
        <DeleteAsoScenarioModal
          asoScenarioId={asoScenarioId}
          asoName={currentAsoName}
          onCancel={() => setOpenedActionModal(undefined)}
        />
      )}
    </>
  );
};

export default AsoScenario;
